import { Suspense } from "react";

import { defineMessages } from "react-intl";

import {
    PriceFinderMessages,
    ProductPlan
} from "@/components/wpl/pricing/src/price-finder/constants";
import PriceFinderSection from "@/components/wpl/pricing/src/price-finder/price-finder";
import { PriceFinderContextProvider } from "@/components/wpl/pricing/src/price-finder/price-finder-context.tsx";
import { createPlanFeatureMessages } from "@/components/wpl/pricing/src/price-finder/utils/create-price-finder-features.tsx";
import { usePricingPlanComparison } from "@/components/wpl/pricing/src/pricing-content-context.tsx";

import { SoftwareCompassPricingPageProps } from "../../../../../../pages/software/compass/pricing/software-compass-pricing-page";

export const SoftwareCompassPriceFinder = (
    props: SoftwareCompassPricingPageProps
) => {
    const content = {
        free: usePricingPlanComparison("Free"),
        standard: usePricingPlanComparison("Standard"),
        premium: usePricingPlanComparison("Premium")
    };

    const freePlanFeatureMessages = createPlanFeatureMessages(content, "free");
    const standardPlanFeatureMessages = createPlanFeatureMessages(
        content,
        "standard"
    );
    const premiumPlanFeatureMessages = createPlanFeatureMessages(
        content,
        "premium"
    );

    const SoftwareCompassPricingProductPlans: ProductPlan[] = [
        {
            planName: content.free?.planTitleExternal || "Free",
            offeringSku: "compass-free",
            description: content.free?.planOneLiner || "",
            recommendationLimit: 0,
            signupLimit: 3,
            signupURLSuffix: "?bundle=compass&edition=free",
            planFeatureMessages: freePlanFeatureMessages
        },
        {
            planName: content.standard?.planTitleExternal || "Standard",
            offeringSku: "compass-standard",
            description: content.standard?.planOneLiner || "",
            recommendationLimit: 3,
            signupLimit: 35000,
            signupURLSuffix: "?bundle=compass&edition=standard",
            planFeatureMessages: standardPlanFeatureMessages
        },
        {
            planName: content.premium?.planTitleExternal || "Premium",
            offeringSku: "compass-premium-sku",
            description: content.premium?.planOneLiner || "",
            recommendationLimit: 250,
            signupLimit: 35000,
            signupURLSuffix: "?bundle=compass&edition=premium",
            planFeatureMessages: premiumPlanFeatureMessages
        }
    ];

    const messages: PriceFinderMessages = defineMessages({
        unitCountInputTextRedesign: {
            id: "CompassPricingPage.priceFinder.unitCountInputTextRedesign",
            defaultMessage: "Team size:"
        },
        cardOverSignupLimit: {
            id: "CompassPricingPage.priceFinder.cardOverSignupLimit",
            defaultMessage: "Your user count exceeds this plan's limit"
        },
        unitCountInputTerm: {
            id: "CompassPricingPage.priceFinder.unitCountInputTerm",
            defaultMessage: "users"
        },
        cardEnterpriseMinimumCount: {
            id: "CompassPricingPage.priceFinder.cardEnterpriseMinimumCount",
            defaultMessage:
                "Enterprise plan pricing is available when you enter {enterpriseMinimumCount} or more users above",
            description:
                "Enterprise is a plan name and should not be translated"
        },
        cardUnitCountTierRange: {
            id: "CompassPricingPage.priceFinder.cardUnitCountTierRange",
            defaultMessage: "User tier: {lowerLimit} - {upperLimit}"
        },
        cardMonthlyPerUnitCount: {
            id: "CompassPricingPage.priceFinder.cardMonthlyPerUnitCount",
            defaultMessage: "per user / month"
        }
    });

    return (
        <Suspense>
            <PriceFinderContextProvider
                dataProductKey="compass"
                productPlans={SoftwareCompassPricingProductPlans}
                defaultUnitCount={300}
            >
                <PriceFinderSection
                    messages={messages}
                    serverSideCCPPricing={
                        props.pricing?.ccpPricingData?.compass
                    }
                    countryCode={props.countryCode}
                    {...props}
                />
            </PriceFinderContextProvider>
        </Suspense>
    );
};
SoftwareCompassPriceFinder.displayName = "SoftwareCompassPriceFinderSection";
